import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useOnboarding } from '../../hooks/useOnboarding';
import { useTasks } from '../../hooks/useTasks';
import {
  X, ChevronRight, ChevronLeft, Inbox, Calendar, Star, Clock, Moon,
  CheckCircle2, Sparkles, Brain, ArrowRight, Plus, Check, Target,
  Users, FileText, RefreshCw, Zap, FolderKanban, HelpCircle, Sunrise,
  Search, Play, Repeat
} from 'lucide-react';

// Brain dump trigger prompts - rotate every 10 seconds
const BRAIN_DUMP_PROMPTS = [
  "What meetings need preparation?",
  "What emails need follow-up?",
  "What commitments have you made recently?",
  "What projects are on your mind?",
  "What's your boss expecting from you?",
  "What deadlines are approaching?",
  "What are you waiting on from others?",
  "Who owes you a response?",
  "Any personal errands to remember?",
  "Anything you've been putting off?",
  "Any ideas you don't want to forget?",
  "What would you do if you had more time?",
  "What's causing you stress right now?",
  "What problem needs solving?"
];

// Status definitions for the statuses step
const STATUS_DEFINITIONS = [
  {
    id: 'inbox',
    name: 'Inbox',
    icon: Inbox,
    description: 'New items needing processing. The starting point for everything you capture.'
  },
  {
    id: 'today',
    name: 'Today',
    icon: Star,
    description: 'Your sacred list. Only tasks you\'re committed to completing today.'
  },
  {
    id: 'scheduled',
    name: 'Scheduled',
    icon: Calendar,
    description: 'Future-dated tasks. They\'ll appear in Today when their time comes.'
  },
  {
    id: 'waiting',
    name: 'Waiting',
    icon: Clock,
    description: 'Delegated or blocked. Set a follow-up date and Helm will remind you.'
  },
  {
    id: 'someday',
    name: 'Someday',
    icon: Moon,
    description: 'Ideas for later. Review these weekly to keep dreams alive.'
  },
  {
    id: 'done',
    name: 'Done',
    icon: CheckCircle2,
    description: 'Completed! Check off tasks to build momentum and track progress.'
  }
];

// Additional features to explore
const ADDITIONAL_FEATURES = [
  {
    id: 'goals',
    name: 'Goals & Projects',
    icon: Target,
    description: 'Set objectives and link tasks to bigger goals'
  },
  {
    id: 'oneOnOnes',
    name: '1:1 Meetings',
    icon: Users,
    description: 'Track talking points and action items with your team'
  },
  {
    id: 'habits',
    name: 'Habits',
    icon: Repeat,
    description: 'Build daily, weekly, and monthly routines'
  },
  {
    id: 'policies',
    name: 'Policies',
    icon: FileText,
    description: 'Personal rules and guidelines for decision-making'
  }
];

export default function OnboardingWizard({ onComplete, onSkip }) {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const {
    currentStep,
    currentStepIndex,
    totalSteps,
    goToNextStep,
    goToPreviousStep,
    completeOnboarding,
    skipOnboarding,
    incrementBrainDumpCount,
    onboarding
  } = useOnboarding();
  const { addTask } = useTasks();

  const firstName = userProfile?.firstName || userProfile?.displayName?.split(' ')[0] || 'there';

  const handleSkip = async () => {
    await skipOnboarding();
    onSkip?.();
  };

  const handleComplete = async (navigateTo = '/') => {
    await completeOnboarding();
    onComplete?.();
    if (navigateTo) {
      navigate(navigateTo);
    }
  };

  // Render the current step
  const renderStep = () => {
    switch (currentStep) {
      case 'welcome':
        return <WelcomeStep firstName={firstName} onNext={goToNextStep} onSkip={handleSkip} />;
      case 'gtd-overview':
        return <GTDOverviewStep onNext={goToNextStep} onBack={goToPreviousStep} onSkip={handleSkip} />;
      case 'brain-dump':
        return <BrainDumpStep
          onNext={goToNextStep}
          onBack={goToPreviousStep}
          onSkip={handleSkip}
          onAddItem={async (title) => {
            await addTask({ title, status: 'inbox' });
            await incrementBrainDumpCount();
          }}
          itemCount={onboarding.brainDumpCount || 0}
        />;
      case 'statuses':
        return <StatusesStep onNext={goToNextStep} onBack={goToPreviousStep} onSkip={handleSkip} />;
      case 'processing-demo':
        return <ProcessingDemoStep onNext={goToNextStep} onBack={goToPreviousStep} onSkip={handleSkip} />;
      case 'calendar':
        return <CalendarStep onNext={goToNextStep} onBack={goToPreviousStep} onSkip={handleSkip} />;
      case 'daily-rhythm':
        return <DailyRhythmStep onNext={goToNextStep} onBack={goToPreviousStep} onSkip={handleSkip} />;
      case 'completion':
        return <CompletionStep
          firstName={firstName}
          itemCount={onboarding.brainDumpCount || 0}
          onComplete={handleComplete}
        />;
      default:
        return <WelcomeStep firstName={firstName} onNext={goToNextStep} onSkip={handleSkip} />;
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 overflow-y-auto">
      {/* Dark overlay that covers EVERYTHING including sidebar */}
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm" />

      {/* Modal */}
      <div className="relative w-full max-w-2xl my-8 z-10">
        {/* Subtle accent glow */}
        <div className="absolute -inset-1 bg-accent/20 rounded-3xl blur-xl opacity-50" />

        <div className="relative bg-surface-card rounded-2xl border border-border shadow-2xl overflow-hidden">
          {/* Progress bar - single color */}
          <div className="h-1 bg-surface-base">
            <div
              className="h-full bg-accent transition-all duration-500"
              style={{ width: `${((currentStepIndex + 1) / totalSteps) * 100}%` }}
            />
          </div>

          {/* Content */}
          <div className="p-6">
            {renderStep()}
          </div>

          {/* Step indicator */}
          <div className="px-6 pb-4 flex items-center justify-center gap-2">
            {Array.from({ length: totalSteps }).map((_, i) => (
              <div
                key={i}
                className={`h-2 rounded-full transition-all ${
                  i === currentStepIndex
                    ? 'w-6 bg-accent'
                    : i < currentStepIndex
                      ? 'w-2 bg-accent/50'
                      : 'w-2 bg-border'
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// Step 1: Welcome
function WelcomeStep({ firstName, onNext, onSkip }) {
  return (
    <div className="text-center">
      {/* Header with single accent color gradient */}
      <div className="relative h-40 -mx-6 -mt-6 mb-6 bg-gradient-to-br from-accent to-accent/70 flex items-center justify-center overflow-hidden">
        <div className="absolute top-[-50%] left-[-20%] w-40 h-40 rounded-full bg-white/10" />
        <div className="absolute bottom-[-60%] right-[-10%] w-48 h-48 rounded-full bg-white/5" />
        <div className="relative w-20 h-20 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center shadow-lg">
          <Sparkles className="w-10 h-10 text-white" />
        </div>
      </div>

      <h2 className="text-3xl font-bold text-text-primary mb-3">
        Welcome, {firstName}!
      </h2>
      <p className="text-lg text-text-secondary mb-6 max-w-md mx-auto">
        In the next 5 minutes, you'll learn a system that transforms how you work. Ready to take the helm?
      </p>

      <div className="bg-surface-base rounded-xl p-5 mb-8 text-left">
        <h3 className="font-semibold text-text-primary mb-3">What you'll learn:</h3>
        <ul className="space-y-2">
          <li className="flex items-center gap-3 text-text-secondary">
            <div className="w-6 h-6 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
              <Check className="w-3 h-3 text-accent" />
            </div>
            Clear your mind with a brain dump
          </li>
          <li className="flex items-center gap-3 text-text-secondary">
            <div className="w-6 h-6 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
              <Check className="w-3 h-3 text-accent" />
            </div>
            Organize tasks with powerful statuses
          </li>
          <li className="flex items-center gap-3 text-text-secondary">
            <div className="w-6 h-6 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
              <Check className="w-3 h-3 text-accent" />
            </div>
            Build a daily review habit
          </li>
        </ul>
      </div>

      <div className="flex gap-3">
        <button
          onClick={onNext}
          className="flex-1 py-4 px-6 bg-accent hover:bg-accent/90 text-white rounded-xl font-semibold shadow-lg shadow-accent/25 hover:shadow-xl hover:shadow-accent/30 transition-all flex items-center justify-center gap-2"
        >
          Get Started
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      <button
        onClick={onSkip}
        className="w-full mt-4 py-2 text-text-muted text-sm hover:text-text-secondary transition-colors"
      >
        Skip for now
      </button>
    </div>
  );
}

// Step 2: GTD Overview - NO EMOJIS, use Lucide icons
function GTDOverviewStep({ onNext, onBack, onSkip }) {
  const steps = [
    { name: 'Capture', icon: Inbox, description: 'Get it out of your head' },
    { name: 'Clarify', icon: HelpCircle, description: 'What is it? Is it actionable?' },
    { name: 'Organize', icon: FolderKanban, description: 'Put it where it belongs' },
    { name: 'Reflect', icon: RefreshCw, description: 'Review regularly' },
    { name: 'Engage', icon: Zap, description: 'Do with confidence' }
  ];

  return (
    <div>
      <div className="text-center mb-8">
        <div className="w-16 h-16 rounded-2xl bg-accent/10 flex items-center justify-center mx-auto mb-4">
          <Brain className="w-8 h-8 text-accent" />
        </div>
        <h2 className="text-2xl font-bold text-text-primary mb-2">
          The GTD Method
        </h2>
        <p className="text-text-secondary max-w-md mx-auto">
          Your brain is for <span className="text-accent font-medium">having ideas</span>, not holding them.
          Here's how Helm helps you capture everything and do it systematically.
        </p>
      </div>

      {/* GTD Flow Diagram - Icons instead of emojis */}
      <div className="flex items-center justify-between gap-2 mb-8 px-4">
        {steps.map((step, i) => {
          const Icon = step.icon;
          return (
            <div key={step.name} className="flex items-center">
              <div className="text-center">
                <div className="w-14 h-14 rounded-xl bg-accent/10 border border-accent/20 flex items-center justify-center mb-2 mx-auto">
                  <Icon className="w-6 h-6 text-accent" />
                </div>
                <div className="text-xs font-medium text-text-primary">{step.name}</div>
              </div>
              {i < steps.length - 1 && (
                <ChevronRight className="w-4 h-4 text-text-muted mx-1 flex-shrink-0" />
              )}
            </div>
          );
        })}
      </div>

      <div className="bg-accent/5 rounded-xl p-5 border border-accent/10 mb-8">
        <p className="text-center text-text-secondary">
          <span className="font-semibold text-text-primary">The key insight:</span> When everything has a place,
          nothing falls through the cracks. You can focus on doing, not remembering.
        </p>
      </div>

      <div className="flex gap-3">
        <button
          onClick={onBack}
          className="py-3 px-4 bg-surface-base border border-border text-text-secondary rounded-xl font-medium hover:bg-surface-hover transition-all"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <button
          onClick={onNext}
          className="flex-1 py-3 px-6 bg-accent hover:bg-accent/90 text-white rounded-xl font-semibold shadow-lg shadow-accent/25 transition-all flex items-center justify-center gap-2"
        >
          Start Brain Dump
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      <button
        onClick={onSkip}
        className="w-full mt-4 py-2 text-text-muted text-sm hover:text-text-secondary transition-colors"
      >
        Skip for now
      </button>
    </div>
  );
}

// Step 3: Brain Dump
function BrainDumpStep({ onNext, onBack, onSkip, onAddItem, itemCount }) {
  const [inputValue, setInputValue] = useState('');
  const [currentPrompt, setCurrentPrompt] = useState(BRAIN_DUMP_PROMPTS[0]);
  const [isAdding, setIsAdding] = useState(false);
  const inputRef = useRef(null);

  // Rotate prompts every 10 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentPrompt(prev => {
        const currentIndex = BRAIN_DUMP_PROMPTS.indexOf(prev);
        const nextIndex = (currentIndex + 1) % BRAIN_DUMP_PROMPTS.length;
        return BRAIN_DUMP_PROMPTS[nextIndex];
      });
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!inputValue.trim() || isAdding) return;

    setIsAdding(true);
    try {
      await onAddItem(inputValue.trim());
      setInputValue('');
      inputRef.current?.focus();
    } finally {
      setIsAdding(false);
    }
  };

  const canProceed = itemCount >= 5;

  return (
    <div>
      <div className="text-center mb-6">
        <div className="w-16 h-16 rounded-2xl bg-accent/10 flex items-center justify-center mx-auto mb-4">
          <Inbox className="w-8 h-8 text-accent" />
        </div>
        <h2 className="text-2xl font-bold text-text-primary mb-2">
          Brain Dump
        </h2>
        <p className="text-text-secondary">
          Empty your mind. Type anything that's taking up mental space.
        </p>
      </div>

      {/* Progress indicator */}
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm text-text-muted">Items captured</span>
        <div className="flex items-center gap-2">
          <span className={`text-lg font-bold ${itemCount >= 10 ? 'text-success' : itemCount >= 5 ? 'text-accent' : 'text-text-muted'}`}>
            {itemCount}
          </span>
          <span className="text-sm text-text-muted">/ 10+ recommended</span>
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-2 bg-surface-base rounded-full mb-6 overflow-hidden">
        <div
          className={`h-full transition-all duration-300 ${itemCount >= 10 ? 'bg-success' : 'bg-accent'}`}
          style={{ width: `${Math.min(itemCount / 10 * 100, 100)}%` }}
        />
      </div>

      {/* Rotating prompt */}
      <div className="bg-surface-base rounded-xl p-4 mb-4 text-center">
        <p className="text-text-muted text-sm mb-1">Try thinking about:</p>
        <p className="text-text-primary font-medium transition-all">{currentPrompt}</p>
      </div>

      {/* Input form */}
      <form onSubmit={handleSubmit} className="mb-6">
        <div className="flex gap-2">
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder="Type anything on your mind..."
            className="flex-1 px-4 py-3 bg-surface-base border border-border rounded-xl text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent"
          />
          <button
            type="submit"
            disabled={!inputValue.trim() || isAdding}
            className="px-4 py-3 bg-accent text-white rounded-xl font-medium hover:bg-accent/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus className="w-5 h-5" />
          </button>
        </div>
      </form>

      {itemCount > 0 && (
        <div className="bg-success/10 border border-success/20 rounded-xl p-4 mb-6">
          <p className="text-success text-center text-sm">
            {itemCount < 5
              ? `Great start! Add ${5 - itemCount} more to continue.`
              : itemCount < 10
                ? `Nice work! Keep going to reach 10 items.`
                : `Excellent! You've captured ${itemCount} items. Your mind is clearer already!`
            }
          </p>
        </div>
      )}

      <div className="flex gap-3">
        <button
          onClick={onBack}
          className="py-3 px-4 bg-surface-base border border-border text-text-secondary rounded-xl font-medium hover:bg-surface-hover transition-all"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <button
          onClick={onNext}
          disabled={!canProceed}
          className="flex-1 py-3 px-6 bg-accent hover:bg-accent/90 text-white rounded-xl font-semibold shadow-lg shadow-accent/25 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {canProceed ? 'Continue' : `Add ${5 - itemCount} more items`}
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      <button
        onClick={onSkip}
        className="w-full mt-4 py-2 text-text-muted text-sm hover:text-text-secondary transition-colors"
      >
        Skip for now
      </button>
    </div>
  );
}

// Step 4: Statuses
function StatusesStep({ onNext, onBack, onSkip }) {
  const [selectedStatus, setSelectedStatus] = useState(null);

  return (
    <div>
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-text-primary mb-2">
          Task Statuses
        </h2>
        <p className="text-text-secondary">
          Every task has a status. Tap each one to learn what it means.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-6">
        {STATUS_DEFINITIONS.map((status) => {
          const Icon = status.icon;
          const isSelected = selectedStatus?.id === status.id;

          return (
            <button
              key={status.id}
              onClick={() => setSelectedStatus(isSelected ? null : status)}
              className={`p-4 rounded-xl border text-left transition-all ${
                isSelected
                  ? 'bg-accent/10 border-accent'
                  : 'bg-surface-base border-border hover:border-accent/50'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
                  <Icon className="w-5 h-5 text-accent" />
                </div>
                <span className="font-medium text-text-primary">{status.name}</span>
              </div>
            </button>
          );
        })}
      </div>

      {/* Selected status description */}
      {selectedStatus && (
        <div className="bg-accent/5 border border-accent/20 rounded-xl p-4 mb-6 animate-in">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
              <selectedStatus.icon className="w-5 h-5 text-accent" />
            </div>
            <div>
              <h4 className="font-semibold text-text-primary mb-1">{selectedStatus.name}</h4>
              <p className="text-sm text-text-secondary">{selectedStatus.description}</p>
            </div>
          </div>
        </div>
      )}

      {!selectedStatus && (
        <div className="bg-surface-base rounded-xl p-4 mb-6 text-center">
          <p className="text-text-muted text-sm">
            Tap a status to see its description
          </p>
        </div>
      )}

      <div className="flex gap-3">
        <button
          onClick={onBack}
          className="py-3 px-4 bg-surface-base border border-border text-text-secondary rounded-xl font-medium hover:bg-surface-hover transition-all"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <button
          onClick={onNext}
          className="flex-1 py-3 px-6 bg-accent hover:bg-accent/90 text-white rounded-xl font-semibold shadow-lg shadow-accent/25 transition-all flex items-center justify-center gap-2"
        >
          Continue
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      <button
        onClick={onSkip}
        className="w-full mt-4 py-2 text-text-muted text-sm hover:text-text-secondary transition-colors"
      >
        Skip for now
      </button>
    </div>
  );
}

// Step 5: Processing Demo - NO EMOJI
function ProcessingDemoStep({ onNext, onBack, onSkip }) {
  const [step, setStep] = useState(0);

  const demoSteps = [
    {
      question: "Is it actionable?",
      options: [
        { label: "Yes, I need to do something", next: 1 },
        { label: "No, it's just information", note: "Delete it or file it as reference" }
      ]
    },
    {
      question: "When should I do it?",
      options: [
        { label: "Today", note: "→ Moves to Today", icon: Star },
        { label: "A specific date", note: "→ Moves to Scheduled", icon: Calendar },
        { label: "Someday, maybe", note: "→ Moves to Someday", icon: Moon },
        { label: "Someone else should do it", note: "→ Moves to Waiting", icon: Clock }
      ]
    }
  ];

  return (
    <div>
      <div className="text-center mb-6">
        <div className="w-16 h-16 rounded-2xl bg-accent/10 flex items-center justify-center mx-auto mb-4">
          <Search className="w-8 h-8 text-accent" />
        </div>
        <h2 className="text-2xl font-bold text-text-primary mb-2">
          Processing = Deciding
        </h2>
        <p className="text-text-secondary">
          For each inbox item, ask yourself these simple questions.
        </p>
      </div>

      {/* Demo flow */}
      <div className="bg-surface-base rounded-xl p-5 mb-6">
        <div className="text-center mb-4">
          <span className="text-sm font-medium text-accent">
            Question {step + 1} of {demoSteps.length}
          </span>
          <h3 className="text-lg font-semibold text-text-primary mt-1">
            {demoSteps[step].question}
          </h3>
        </div>

        <div className="space-y-2">
          {demoSteps[step].options.map((option, i) => {
            const Icon = option.icon;
            return (
              <button
                key={i}
                onClick={() => {
                  if (option.next !== undefined) {
                    setStep(option.next);
                  }
                }}
                className="w-full p-4 bg-surface-card border border-border rounded-xl text-left hover:border-accent/50 transition-all"
              >
                <div className="flex items-center gap-3">
                  {Icon && (
                    <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center">
                      <Icon className="w-4 h-4 text-accent" />
                    </div>
                  )}
                  <div>
                    <span className="font-medium text-text-primary">{option.label}</span>
                    {option.note && (
                      <p className="text-sm text-text-muted mt-0.5">{option.note}</p>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {step > 0 && (
          <button
            onClick={() => setStep(0)}
            className="w-full mt-3 py-2 text-text-muted text-sm hover:text-text-secondary transition-colors"
          >
            ← Start over
          </button>
        )}
      </div>

      <div className="bg-accent/5 rounded-xl p-4 border border-accent/10 mb-6">
        <p className="text-center text-sm text-text-secondary">
          <span className="font-semibold text-text-primary">Pro tip:</span> Process quickly.
          Don't perfect—just decide and move on.
        </p>
      </div>

      <div className="flex gap-3">
        <button
          onClick={onBack}
          className="py-3 px-4 bg-surface-base border border-border text-text-secondary rounded-xl font-medium hover:bg-surface-hover transition-all"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <button
          onClick={onNext}
          className="flex-1 py-3 px-6 bg-accent hover:bg-accent/90 text-white rounded-xl font-semibold shadow-lg shadow-accent/25 transition-all flex items-center justify-center gap-2"
        >
          Continue
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      <button
        onClick={onSkip}
        className="w-full mt-4 py-2 text-text-muted text-sm hover:text-text-secondary transition-colors"
      >
        Skip for now
      </button>
    </div>
  );
}

// Step 6: Calendar
function CalendarStep({ onNext, onBack, onSkip }) {
  return (
    <div>
      <div className="text-center mb-6">
        <div className="w-16 h-16 rounded-2xl bg-accent/10 flex items-center justify-center mx-auto mb-4">
          <Calendar className="w-8 h-8 text-accent" />
        </div>
        <h2 className="text-2xl font-bold text-text-primary mb-2">
          Time Blocking
        </h2>
        <p className="text-text-secondary">
          Connect your calendar to see if your day is realistic.
        </p>
      </div>

      <div className="space-y-3 mb-6">
        <div className="bg-surface-base rounded-xl p-4">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
              <Check className="w-4 h-4 text-accent" />
            </div>
            <div>
              <h4 className="font-medium text-text-primary">See meetings alongside tasks</h4>
              <p className="text-sm text-text-muted">Know your true available time</p>
            </div>
          </div>
        </div>

        <div className="bg-surface-base rounded-xl p-4">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
              <Check className="w-4 h-4 text-accent" />
            </div>
            <div>
              <h4 className="font-medium text-text-primary">Assign tasks to time blocks</h4>
              <p className="text-sm text-text-muted">Plan when you'll actually do the work</p>
            </div>
          </div>
        </div>

        <div className="bg-surface-base rounded-xl p-4">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
              <Check className="w-4 h-4 text-accent" />
            </div>
            <div>
              <h4 className="font-medium text-text-primary">Spot overcommitment early</h4>
              <p className="text-sm text-text-muted">Adjust before it's too late</p>
            </div>
          </div>
        </div>
      </div>

      <button
        className="w-full py-3 px-4 bg-surface-base border border-border text-text-primary rounded-xl font-medium hover:bg-surface-hover transition-all flex items-center justify-center gap-2 mb-4"
      >
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
        </svg>
        Connect Google Calendar
      </button>

      <div className="flex gap-3">
        <button
          onClick={onBack}
          className="py-3 px-4 bg-surface-base border border-border text-text-secondary rounded-xl font-medium hover:bg-surface-hover transition-all"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <button
          onClick={onNext}
          className="flex-1 py-3 px-6 bg-accent hover:bg-accent/90 text-white rounded-xl font-semibold shadow-lg shadow-accent/25 transition-all flex items-center justify-center gap-2"
        >
          Continue
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      <button
        onClick={onSkip}
        className="w-full mt-4 py-2 text-text-muted text-sm hover:text-text-secondary transition-colors"
      >
        Skip for now
      </button>
    </div>
  );
}

// Step 7: Daily Rhythm - NO EMOJI
function DailyRhythmStep({ onNext, onBack, onSkip }) {
  return (
    <div>
      <div className="text-center mb-6">
        <div className="w-16 h-16 rounded-2xl bg-accent/10 flex items-center justify-center mx-auto mb-4">
          <Sunrise className="w-8 h-8 text-accent" />
        </div>
        <h2 className="text-2xl font-bold text-text-primary mb-2">
          The Daily Review
        </h2>
        <p className="text-text-secondary">
          This 5-minute habit is the secret to staying ahead of work.
        </p>
      </div>

      <div className="bg-accent/5 rounded-xl p-5 border border-accent/10 mb-6">
        <h3 className="font-semibold text-text-primary mb-4 text-center">
          Every evening, take 5 minutes to:
        </h3>
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-accent/20 flex items-center justify-center flex-shrink-0 text-sm font-bold text-accent">
              1
            </div>
            <span className="text-text-secondary">
              <span className="font-medium text-text-primary">Process your inbox</span> — decide on new items
            </span>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-accent/20 flex items-center justify-center flex-shrink-0 text-sm font-bold text-accent">
              2
            </div>
            <span className="text-text-secondary">
              <span className="font-medium text-text-primary">Review today</span> — what got done? What didn't?
            </span>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-accent/20 flex items-center justify-center flex-shrink-0 text-sm font-bold text-accent">
              3
            </div>
            <span className="text-text-secondary">
              <span className="font-medium text-text-primary">Plan tomorrow</span> — what will you commit to?
            </span>
          </div>
        </div>
      </div>

      {/* More features to explore */}
      <div className="bg-surface-base rounded-xl p-4 mb-6">
        <h4 className="font-medium text-text-primary mb-3 text-center text-sm">
          More features to explore later:
        </h4>
        <div className="grid grid-cols-2 gap-2">
          {ADDITIONAL_FEATURES.map((feature) => {
            const Icon = feature.icon;
            return (
              <div key={feature.id} className="flex items-center gap-2 p-2">
                <div className="w-6 h-6 rounded bg-accent/10 flex items-center justify-center flex-shrink-0">
                  <Icon className="w-3 h-3 text-accent" />
                </div>
                <span className="text-xs text-text-secondary">{feature.name}</span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex gap-3">
        <button
          onClick={onBack}
          className="py-3 px-4 bg-surface-base border border-border text-text-secondary rounded-xl font-medium hover:bg-surface-hover transition-all"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <button
          onClick={onNext}
          className="flex-1 py-3 px-6 bg-accent hover:bg-accent/90 text-white rounded-xl font-semibold shadow-lg shadow-accent/25 transition-all flex items-center justify-center gap-2"
        >
          Finish Setup
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      <button
        onClick={onSkip}
        className="w-full mt-4 py-2 text-text-muted text-sm hover:text-text-secondary transition-colors"
      >
        Skip for now
      </button>
    </div>
  );
}

// Step 8: Completion - Fixed confetti
function CompletionStep({ firstName, itemCount, onComplete }) {
  const [showConfetti, setShowConfetti] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setShowConfetti(false), 4000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="text-center relative">
      {/* Confetti effect - positioned to be visible */}
      {showConfetti && (
        <div className="absolute inset-0 pointer-events-none overflow-hidden -mx-6 -my-6">
          {Array.from({ length: 60 }).map((_, i) => (
            <div
              key={i}
              className="absolute confetti-piece"
              style={{
                left: `${Math.random() * 100}%`,
                top: '-10px',
                animationDelay: `${Math.random() * 1.5}s`,
                animationDuration: `${2.5 + Math.random() * 1.5}s`
              }}
            >
              <div
                className="w-2 h-2 rounded-sm"
                style={{
                  backgroundColor: `hsl(${20 + Math.random() * 30}, ${60 + Math.random() * 30}%, ${50 + Math.random() * 20}%)`
                }}
              />
            </div>
          ))}
        </div>
      )}

      {/* Header - single accent color */}
      <div className="relative h-32 -mx-6 -mt-6 mb-6 bg-gradient-to-br from-accent to-accent/70 flex items-center justify-center overflow-hidden">
        <div className="absolute top-[-50%] left-[-20%] w-40 h-40 rounded-full bg-white/10" />
        <div className="absolute bottom-[-60%] right-[-10%] w-48 h-48 rounded-full bg-white/5" />
        <div className="relative w-20 h-20 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center shadow-lg">
          <CheckCircle2 className="w-10 h-10 text-white" />
        </div>
      </div>

      <h2 className="text-3xl font-bold text-text-primary mb-3">
        You're Ready, {firstName}!
      </h2>

      {itemCount > 0 && (
        <div className="inline-flex items-center gap-2 bg-accent/10 text-accent px-4 py-2 rounded-full mb-4">
          <Inbox className="w-4 h-4" />
          <span className="font-medium">{itemCount} items captured in your inbox</span>
        </div>
      )}

      <p className="text-text-secondary mb-8 max-w-sm mx-auto">
        Your productivity system is set up. Now let's put it to work.
      </p>

      <div className="space-y-3 mb-8">
        <button
          onClick={() => onComplete('/tasks?status=inbox')}
          className="w-full py-4 px-6 bg-accent hover:bg-accent/90 text-white rounded-xl font-semibold shadow-lg shadow-accent/25 transition-all flex items-center justify-center gap-2"
        >
          <Inbox className="w-5 h-5" />
          Process My Inbox
          <ArrowRight className="w-5 h-5" />
        </button>

        <button
          onClick={() => onComplete('/')}
          className="w-full py-3 px-6 bg-surface-base border border-border text-text-primary rounded-xl font-medium hover:bg-surface-hover transition-all"
        >
          Go to Dashboard
        </button>
      </div>

      <p className="text-text-muted text-sm">
        Remember: Do your Daily Review tonight to stay on top of everything!
      </p>

      {/* CSS for confetti */}
      <style>{`
        @keyframes confetti-fall {
          0% {
            transform: translateY(0) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(500px) rotate(720deg);
            opacity: 0;
          }
        }
        .confetti-piece {
          animation: confetti-fall linear forwards;
        }
      `}</style>
    </div>
  );
}
